import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  X,
  Bell,
  UserPlus,
  Settings,
  Shield,
  FileText,
  Info,
  Trash2,
  ChevronDown,
  UserX,
  AlertTriangle,
  MessageCircle,
  Phone,
  Store,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { ChatModal } from './ChatModal';

const APP_VERSION = '2.1.0';

/**
 * SideMenu — slide-in drawer triggered by header menu icon.
 *
 * Items:
 *   შეტყობინებები → /notifications (red dot if unread > 0)
 *   ─────────────────────
 *   ადმინ პანელი → /admin (admin/moderator only)
 *   რეგისტრაცია / გასვლა
 *   პარამეტრები → expandable inline (privacy, terms, version, clear cache)
 */
export function SideMenu({ open, onClose }) {
  const { user, logout, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const { unreadCount, chatUnread } = useNotifications();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  // Close on ESC.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Collapse settings whenever the drawer closes — fresh state on next open.
  useEffect(() => {
    if (!open) setSettingsOpen(false);
  }, [open]);

  const clearCache = () => {
    const keys = [
      'pricemap_favorites',
      'pricemap_geo_decision',
      'pricemap_chat_session_id',
    ];
    keys.forEach((k) => localStorage.removeItem(k));
    toast.success('მონაცემები გასუფთავდა');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-50 bg-black/40 transition-opacity duration-200 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        data-testid="side-menu-backdrop"
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 z-50 h-full w-[82%] max-w-[340px] bg-white shadow-2xl transform transition-transform duration-300 ease-out overflow-y-auto ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="side-menu"
        aria-hidden={open ? undefined : true}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-5 h-14 border-b border-slate-100 sticky top-0 bg-white z-10">
          <span className="text-base font-semibold text-slate-800 tracking-tight">
            მენიუ
          </span>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-slate-100 active:scale-95 flex items-center justify-center transition-all"
            data-testid="side-menu-close"
            aria-label="დახურვა"
          >
            <X className="w-5 h-5 text-slate-600" strokeWidth={1.7} />
          </button>
        </div>

        {/* Drawer body */}
        <nav className="py-2">
          {/* Notifications + admin chat — authenticated users only */}
          {user && (
          <>
          <button
            onClick={() => { navigate('/notifications'); onClose(); }}
            className="w-full flex items-center gap-3.5 px-5 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition-colors"
            data-testid="menu-notifications"
          >
            <div className="relative">
              <Bell className="w-5 h-5 text-slate-500" strokeWidth={1.7} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 ring-1 ring-white" />
              )}
            </div>
            <span className="text-sm font-medium text-slate-800">
              შეტყობინებები
            </span>
            {unreadCount > 0 && (
              <span className="ml-auto text-[11px] font-semibold text-red-500">
                {unreadCount}
              </span>
            )}
          </button>

          {/* Chat with admin — opens ChatModal */}
          <button
            onClick={() => { onClose(); setChatOpen(true); }}
            className="w-full flex items-center gap-3.5 px-5 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition-colors"
            data-testid="menu-chat"
          >
            <div className="relative">
              <MessageCircle className="w-5 h-5 text-slate-500" strokeWidth={1.7} />
              {chatUnread > 0 && (
                <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 ring-1 ring-white" />
              )}
            </div>
            <span className="text-sm font-medium text-slate-800">
              ჩატი ადმინთან
            </span>
            {chatUnread > 0 && (
              <span className="ml-auto text-[11px] font-semibold text-red-500">
                {chatUnread}
              </span>
            )}
          </button>
          </>
          )}

          {/* Divider */}
          <div className="border-t border-slate-100 my-1" />

          {/* Admin panel link — only when user has admin/moderator role */}
          {user && (user.role === 'admin' || user.role === 'moderator') && (
            <Link
              to="/admin"
              onClick={onClose}
              className="w-full flex items-center gap-3.5 px-5 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition-colors"
              data-testid="menu-admin"
            >
              <Shield className="w-5 h-5 text-teal-600" strokeWidth={1.7} />
              <span className="text-sm font-medium text-slate-800">
                ადმინ პანელი
              </span>
            </Link>
          )}

          {/* Auth row */}
          {!user ? (
            <Link
              to="/auth"
              state={{ from: 'nav', mode: 'login' }}
              onClick={onClose}
              className="w-full flex items-center gap-3.5 px-5 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition-colors"
              data-testid="menu-login"
            >
              <UserPlus className="w-5 h-5 text-teal-600" strokeWidth={1.7} />
              <span className="text-sm font-medium text-slate-800">შესვლა / რეგისტრაცია</span>
            </Link>
          ) : (
            <button
              onClick={() => { logout(); onClose(); }}
              className="w-full flex items-center gap-3.5 px-5 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition-colors"
              data-testid="menu-logout"
            >
              <UserPlus className="w-5 h-5 text-slate-500" strokeWidth={1.7} />
              <span className="text-sm font-medium text-slate-800">
                გასვლა ({user.name || 'მომხმარებელი'})
              </span>
            </button>
          )}

          <Link
            to="/for-business"
            onClick={onClose}
            className="w-full flex items-center gap-3.5 px-5 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition-colors"
            data-testid="menu-for-business"
          >
            <Store className="w-5 h-5 text-slate-500" strokeWidth={1.7} />
            <span className="text-sm font-medium text-slate-800">ბიზნესისთვის</span>
          </Link>

          {/* Settings — expandable inline */}
          <button
            onClick={() => setSettingsOpen((prev) => !prev)}
            className="w-full flex items-center gap-3.5 px-5 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition-colors"
            data-testid="menu-settings"
            aria-expanded={settingsOpen}
          >
            <Settings className="w-5 h-5 text-slate-500" strokeWidth={1.7} />
            <span className="text-sm font-medium text-slate-800">
              პარამეტრები
            </span>
            <ChevronDown
              className={`ml-auto w-4 h-4 text-slate-400 transition-transform duration-200 ${
                settingsOpen ? 'rotate-180' : ''
              }`}
              strokeWidth={1.7}
            />
          </button>

          {/* Expanded settings content */}
          {settingsOpen && (
            <div className="bg-slate-50 border-y border-slate-100" data-testid="settings-panel">
              <Link
                to="/privacy"
                onClick={onClose}
                className="w-full flex items-center gap-3 pl-12 pr-5 py-3 hover:bg-white active:bg-white transition-colors"
              >
                <Shield className="w-4 h-4 text-slate-500" strokeWidth={1.7} />
                <span className="text-[13px] text-slate-700">
                  კონფიდენციალურობის პოლიტიკა
                </span>
              </Link>
              <Link
                to="/terms"
                onClick={onClose}
                className="w-full flex items-center gap-3 pl-12 pr-5 py-3 hover:bg-white active:bg-white transition-colors"
              >
                <FileText className="w-4 h-4 text-slate-500" strokeWidth={1.7} />
                <span className="text-[13px] text-slate-700">
                  მომსახურების პირობები
                </span>
              </Link>
              <Link
                to="/contact"
                onClick={onClose}
                className="w-full flex items-center gap-3 pl-12 pr-5 py-3 hover:bg-white active:bg-white transition-colors"
              >
                <Phone className="w-4 h-4 text-slate-500" strokeWidth={1.7} />
                <span className="text-[13px] text-slate-700">
                  კონტაქტი
                </span>
              </Link>
              <div className="flex items-center gap-3 pl-12 pr-5 py-3">
                <Info className="w-4 h-4 text-slate-500" strokeWidth={1.7} />
                <span className="text-[13px] text-slate-700">ვერსია</span>
                <span className="ml-auto text-[12px] text-slate-500">
                  {APP_VERSION}
                </span>
              </div>
              <button
                onClick={clearCache}
                className="w-full flex items-center gap-3 pl-12 pr-5 py-3 hover:bg-white active:bg-white transition-colors"
                data-testid="settings-clear-cache"
              >
                <Trash2 className="w-4 h-4 text-red-500" strokeWidth={1.7} />
                <span className="text-[13px] text-red-600">
                  ქეშის გასუფთავება
                </span>
              </button>
              {user && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="w-full flex items-center gap-3 pl-12 pr-5 py-3 hover:bg-white active:bg-white transition-colors"
                >
                  <UserX className="w-4 h-4 text-red-500" strokeWidth={1.7} />
                  <span className="text-[13px] text-red-600">
                    ანგარიშის წაშლა
                  </span>
                </button>
              )}
            </div>
          )}
        </nav>
      </aside>

      {/* Delete account confirm dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <h2 className="text-base font-semibold text-slate-800">ანგარიშის წაშლა</h2>
            </div>
            <p className="text-sm text-slate-600 mb-6 leading-relaxed">
              ეს მოქმედება შეუქცევადია. თქვენი ყველა მონაცემი სამუდამოდ წაიშლება.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="flex-1 h-11 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                გაუქმება
              </button>
              <button
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await deleteAccount();
                  } catch (error) {
                    toast.error(error.message);
                    setDeleting(false);
                    setConfirmDelete(false);
                  }
                }}
                disabled={deleting}
                className="flex-1 h-11 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
              >
                {deleting ? 'იშლება...' : 'წაშლა'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat modal (state persists; renders on top of menu/backdrop) */}
      <ChatModal
        open={chatOpen}
        onClose={() => setChatOpen(false)}
      />
    </>
  );
}

export default SideMenu;
